import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Drawer,
  Chip,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  BookOnline as BookingsIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  Repeat as RepeatIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  RateReview as ReviewIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { accountService } from '../services/accountService';

const DRAWER_WIDTH = 280;

const ClientDashboard = () => {
  const [currentTab, setCurrentTab] = useState(1); // Start with bookings tab
  const [mobileOpen, setMobileOpen] = useState(false);
  const [success, setSuccess] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteForm, setDeleteForm] = useState({
    password: '',
    confirmDelete: false
  });
  
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation();

  const tabs = [
    { id: 0, label: 'Mon Profil', icon: PersonIcon },
    { id: 1, label: 'Mes Réservations', icon: BookingsIcon },
    { id: 2, label: 'Historique', icon: HistoryIcon },
    { id: 3, label: 'Rechercher un Salon', icon: SearchIcon },
    { id: 4, label: 'Paramètres', icon: SettingsIcon },
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    
    // Load user bookings
    loadBookings();
  }, [currentUser, navigate]);

  const loadBookings = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      // Query bookings where the client email matches current user email
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('clientEmail', '==', currentUser.email),
        orderBy('date', 'desc')
      );
      
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingsData = [];
      
      for (const bookingDoc of bookingsSnapshot.docs) {
        const booking = { id: bookingDoc.id, ...bookingDoc.data() };
        
        // Get salon details
        try {
          const salonDoc = await getDoc(doc(db, 'users', booking.salonId));
          if (salonDoc.exists()) {
            booking.salonDetails = salonDoc.data();
          }
        } catch (error) {
          console.error('Error fetching salon details:', error);
        }
        
        bookingsData.push(booking);
      }
      
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBookingStatus = (booking) => {
    const now = new Date();
    const bookingDate = booking.date?.toDate ? booking.date.toDate() : new Date(booking.date);
    
    if (booking.status === 'cancelled') return 'cancelled';
    if (booking.status === 'rejected') return 'rejected';
    if (bookingDate < now) return 'completed';
    
    const timeDiff = bookingDate - now;
    const hoursDiff = timeDiff / (1000 * 60 * 60);
    
    if (hoursDiff <= 24) return 'upcoming';
    return 'confirmed';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'warning';
      case 'confirmed': return 'success';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'upcoming': return 'Bientôt';
      case 'confirmed': return 'Confirmé';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      case 'rejected': return 'Refusé';
      default: return 'En attente';
    }
  };

  const handleRebookAppointment = (booking) => {
    // Navigate to salon page for rebooking
    navigate(`/salon/${booking.salonId}`);
  };

  const handleLeaveReview = (booking) => {
    setSelectedBooking(booking);
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    // This would typically submit to Google My Business or internal review system
    // For now, we'll just show a success message
    setSuccess('Merci pour votre avis ! Il sera publié prochainement.');
    setReviewDialogOpen(false);
    setSelectedBooking(null);
    setReviewRating(5);
    setReviewComment('');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteForm.password) {
      setSuccess(''); // Clear any success message
      // We'll show error in the dialog
      return;
    }

    if (!deleteForm.confirmDelete) {
      setSuccess(''); // Clear any success message
      // We'll show error in the dialog
      return;
    }

    setLoading(true);
    try {
      // Vérifier d'abord si le compte peut être supprimé
      const canDelete = accountService.canDeleteAccount(userProfile);
      if (!canDelete.canDelete) {
        setSuccess(''); // Clear any success message
        return;
      }

      // Supprimer le compte
      await accountService.deleteAccount(currentUser, userProfile);
      
      // Rediriger vers la page d'accueil
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error deleting account:', error);
      setSuccess(''); // Clear any success message
    } finally {
      setLoading(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (!userProfile) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: '#fafaf9'
        }}
      >
        <Box textAlign="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              border: '3px solid #e7e5e4',
              borderTop: '3px solid #1c1917',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              mx: 'auto',
              mb: 2,
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          <Typography color="text.secondary">Chargement...</Typography>
        </Box>
      </Box>
    );
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid #e7e5e4' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#f5f5dc',
              color: '#1c1917',
              fontSize: '1.5rem',
              fontWeight: 700,
              mr: 2
            }}
          >
            {userProfile.firstName?.[0] || userProfile.ownerName?.[0] || 'C'}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1c1917' }}>
              {userProfile.firstName && userProfile.lastName 
                ? `${userProfile.firstName} ${userProfile.lastName}`
                : userProfile.ownerName || 'Client'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Membre Client
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <ListItem
                key={tab.id}
                button
                selected={currentTab === tab.id}
                onClick={() => {
                  setCurrentTab(tab.id);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  mx: 2,
                  mb: 1,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: '#f5f5dc',
                    color: '#1c1917',
                    '&:hover': {
                      bgcolor: '#f5f5dc',
                    }
                  },
                  '&:hover': {
                    bgcolor: '#f5f5f4'
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <Icon />
                </ListItemIcon>
                <ListItemText 
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontWeight: currentTab === tab.id ? 600 : 400
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Divider />

      {/* Footer Actions */}
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          startIcon={<HomeIcon />}
          onClick={handleGoHome}
          sx={{
            mb: 1,
            justifyContent: 'flex-start',
            color: '#57534e',
            '&:hover': {
              bgcolor: '#f5f5f4'
            }
          }}
        >
          Retour à l'accueil
        </Button>
        <Button
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            justifyContent: 'flex-start',
            color: '#dc2626',
            '&:hover': {
              bgcolor: '#fef2f2'
            }
          }}
        >
          Se déconnecter
        </Button>
      </Box>
    </Box>
  );

  const renderBookingCard = (booking) => {
    const status = getBookingStatus(booking);
    const bookingDate = booking.date?.toDate ? booking.date.toDate() : new Date(booking.date);
    
    return (
      <Card key={booking.id} sx={{ mb: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {booking.salonDetails?.salonName || 'Salon'}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {booking.salonDetails?.address || booking.salonDetails?.city || 'Adresse non disponible'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {bookingDate.toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} à {booking.time}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={getStatusLabel(status)}
              color={getStatusColor(status)}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Service:</strong> {booking.service || 'Service non spécifié'}
          </Typography>
          
          {booking.notes && (
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
              <strong>Notes:</strong> {booking.notes}
            </Typography>
          )}
          
          <Box display="flex" gap={1} flexWrap="wrap">
            {status === 'completed' && (
              <>
                <Button
                  size="small"
                  startIcon={<RepeatIcon />}
                  onClick={() => handleRebookAppointment(booking)}
                  sx={{ color: '#1c1917' }}
                >
                  Reprendre RDV
                </Button>
                {booking.salonDetails?.googleMapsUrl && (
                  <Button
                    size="small"
                    startIcon={<ReviewIcon />}
                    onClick={() => handleLeaveReview(booking)}
                    sx={{ color: '#d97706' }}
                  >
                    Laisser un avis
                  </Button>
                )}
              </>
            )}
            {(status === 'confirmed' || status === 'upcoming') && (
              <Button
                size="small"
                startIcon={<RepeatIcon />}
                onClick={() => handleRebookAppointment(booking)}
                sx={{ color: '#1c1917' }}
              >
                Modifier/Annuler
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 0: // Mon Profil
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Mon Profil
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Informations personnelles
                      </Typography>
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Prénom
                          </Typography>
                          <Typography variant="body1">
                            {userProfile.firstName || 'Non renseigné'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Nom
                          </Typography>
                          <Typography variant="body1">
                            {userProfile.lastName || 'Non renseigné'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailIcon color="action" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Email
                            </Typography>
                            <Typography variant="body1">
                              {userProfile.email}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon color="action" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Téléphone
                            </Typography>
                            <Typography variant="body1">
                              {userProfile.phone || 'Non renseigné'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: '#f5f5dc',
                        color: '#1c1917',
                        fontSize: '2rem',
                        fontWeight: 700,
                        mx: 'auto',
                        mb: 2
                      }}
                    >
                      {userProfile.firstName?.[0] || userProfile.ownerName?.[0] || 'C'}
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      {userProfile.firstName && userProfile.lastName
                        ? `${userProfile.firstName} ${userProfile.lastName}`
                        : userProfile.ownerName || 'Client'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Membre depuis {new Date(userProfile.createdAt?.toDate?.() || userProfile.createdAt).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Mes Réservations (Prochaines)
        const upcomingBookings = bookings.filter(booking => {
          const status = getBookingStatus(booking);
          return status === 'confirmed' || status === 'upcoming';
        });

        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Mes Prochaines Réservations
            </Typography>
            
            {loading ? (
              <Card>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      border: '3px solid #e7e5e4',
                      borderTop: '3px solid #1c1917',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      mx: 'auto',
                      mb: 2,
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  />
                  <Typography color="text.secondary">Chargement de vos réservations...</Typography>
                </CardContent>
              </Card>
            ) : upcomingBookings.length > 0 ? (
              <Box>
                {upcomingBookings.map(renderBookingCard)}
              </Box>
            ) : (
              <Card>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <CalendarIcon sx={{ fontSize: 64, color: '#d6d3d1', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Aucune réservation à venir
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Vous n'avez pas de réservations prévues. Trouvez un salon et prenez votre prochain rendez-vous !
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setCurrentTab(3)}
                    sx={{
                      bgcolor: '#1c1917',
                      '&:hover': { bgcolor: '#292524' }
                    }}
                  >
                    Rechercher un salon
                  </Button>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 2: // Historique
        const pastBookings = bookings.filter(booking => {
          const status = getBookingStatus(booking);
          return status === 'completed' || status === 'cancelled' || status === 'rejected';
        });

        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Historique des Réservations
            </Typography>
            
            {loading ? (
              <Card>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      border: '3px solid #e7e5e4',
                      borderTop: '3px solid #1c1917',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      mx: 'auto',
                      mb: 2,
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  />
                  <Typography color="text.secondary">Chargement de votre historique...</Typography>
                </CardContent>
              </Card>
            ) : pastBookings.length > 0 ? (
              <Box>
                {pastBookings.map(renderBookingCard)}
              </Box>
            ) : (
              <Card>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <HistoryIcon sx={{ fontSize: 64, color: '#d6d3d1', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Aucun historique
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Vous n'avez pas encore d'historique de réservations.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setCurrentTab(3)}
                    sx={{
                      bgcolor: '#1c1917',
                      '&:hover': { bgcolor: '#292524' }
                    }}
                  >
                    Prendre un premier rendez-vous
                  </Button>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 3: // Rechercher un Salon
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Rechercher un Salon
            </Typography>
            
            <Card>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 64, color: '#d6d3d1', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Trouvez votre salon idéal
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Découvrez les meilleurs salons de beauté près de chez vous et prenez rendez-vous en quelques clics.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleGoHome}
                  sx={{
                    bgcolor: '#1c1917',
                    '&:hover': { bgcolor: '#292524' }
                  }}
                >
                  Voir tous les salons
                </Button>
              </CardContent>
            </Card>
          </Box>
        );

      case 4: // Paramètres
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Paramètres
            </Typography>
            
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Paramètres du compte
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Modifier mes informations"
                      secondary="Nom, prénom, téléphone"
                    />
                    <IconButton>
                      <EditIcon />
                    </IconButton>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Notifications par email"
                      secondary="Rappels de rendez-vous et demandes d'avis"
                    />
                    <IconButton>
                      <EditIcon />
                    </IconButton>
                  </ListItem>
                  <Divider />
                  <ListItem button onClick={() => setDeleteDialogOpen(true)}>
                    <ListItemIcon>
                      <DeleteIcon sx={{ color: '#dc2626' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Supprimer mon compte"
                      secondary="Cette action est irréversible"
                      primaryTypographyProps={{ color: '#dc2626' }}
                      secondaryTypographyProps={{ color: '#7f1d1d' }}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem button onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Se déconnecter"
                      primaryTypographyProps={{ color: 'error' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fafaf9' }}>
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f6f3 50%, #f1ede8 100%)',
            color: '#1c1917',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.1)',
            borderBottom: '1px solid rgba(231, 229, 228, 0.6)',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Mon Espace Client
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: 'white',
              borderRight: '1px solid #e7e5e4',
              boxShadow: isMobile ? 3 : 'none'
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: isMobile ? 8 : 0
        }}
      >
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}
          
          {renderTabContent()}
        </Container>
      </Box>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Laisser un avis pour {selectedBooking?.salonDetails?.salonName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Comment s'est passé votre rendez-vous ?
            </Typography>
            <Rating
              value={reviewRating}
              onChange={(event, newValue) => setReviewRating(newValue)}
              size="large"
              sx={{ mt: 1 }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Votre commentaire (optionnel)"
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            placeholder="Partagez votre expérience avec ce salon..."
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Votre avis sera publié sur Google My Business si le salon a configuré ce service.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            sx={{
              bgcolor: '#1c1917',
              '&:hover': { bgcolor: '#292524' }
            }}
          >
            Publier l'avis
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 600,
          color: '#dc2626',
          fontSize: { xs: '1.125rem', md: '1.25rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon />
          Confirmer la suppression du compte
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Cette action est irréversible !
            </Typography>
            <Typography variant="body2">
              Votre compte client et toutes vos réservations seront définitivement supprimés.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirmez votre mot de passe"
                type="password"
                value={deleteForm.password}
                onChange={(e) => setDeleteForm(prev => ({ ...prev, password: e.target.value }))}
                helperText="Saisissez votre mot de passe pour confirmer la suppression"
                error={!deleteForm.password && deleteForm.password !== ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: '#dc2626',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#dc2626',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteForm.confirmDelete}
                    onChange={(e) => setDeleteForm(prev => ({ ...prev, confirmDelete: e.target.checked }))}
                    sx={{
                      color: '#dc2626',
                      '&.Mui-checked': {
                        color: '#dc2626',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Je comprends que cette action est irréversible et je souhaite supprimer définitivement mon compte
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteForm({ password: '', confirmDelete: false });
            }}
            sx={{
              color: '#6B7280',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            disabled={loading || !deleteForm.password || !deleteForm.confirmDelete}
            startIcon={<DeleteIcon />}
            sx={{
              backgroundColor: '#dc2626',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#b91c1c'
              },
              '&:disabled': {
                backgroundColor: '#9ca3af',
                color: '#ffffff'
              }
            }}
          >
            {loading ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDashboard;